
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { format } from "date-fns";
import { TrainingSession } from "../types";
import { Video, X } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { YouTubeVideoPlayer } from "./YouTubeVideoPlayer";
import { CompetitionDetails } from "./session-details/CompetitionDetails";
import { SessionDialogArrows } from "./session-details/SessionDialogArrows";
import { SessionMetadata } from "./session-details/SessionMetadata";
import { SessionTechniques } from "./session-details/SessionTechniques";
import { SessionReflections } from "./session-details/SessionReflections";

interface SessionDetailsDialogProps {
  session: TrainingSession | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (session: TrainingSession) => void;

  // Optional for micro timeline navigation
  sessions?: TrainingSession[];
  currentIndex?: number;
  onNavigate?: (newIndex: number) => void;
}

export function SessionDetailsDialog({ 
  session, 
  open, 
  onOpenChange,
  onEdit,
  sessions,
  currentIndex,
  onNavigate
}: SessionDetailsDialogProps) {
  if (!session) return null;

  const formattedDate = format(new Date(session.date), "MMMM d, yyyy");
  const formattedDuration = `${session.duration} min`;
  const isCompetition = session.title === 'Competition' || session.sessionType === 'Competition';

  const handleEdit = () => {
    if (onEdit && session) {
      onEdit(session);
      onOpenChange(false);
    }
  };

  const canGoPrev =
    sessions && typeof currentIndex === "number" && currentIndex > 0;
  const canGoNext =
    sessions &&
    typeof currentIndex === "number" &&
    sessions.length > 0 &&
    currentIndex < sessions.length - 1;

  const handlePrev = () => {
    if (canGoPrev && onNavigate && currentIndex !== undefined) {
      onNavigate(currentIndex - 1);
    }
  };
  const handleNext = () => {
    if (canGoNext && onNavigate && currentIndex !== undefined) {
      onNavigate(currentIndex + 1);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-w-[95vw] max-h-[90vh] overflow-y-auto text-left p-4 sm:p-6 [&>button]:hidden">
        {/* Custom Navigation and Close Controls */}
        {sessions && typeof currentIndex === "number" && (
          <div className="flex justify-between items-center mb-4 relative">
            <SessionDialogArrows
              canGoPrev={canGoPrev || false}
              canGoNext={canGoNext || false}
              onPrev={handlePrev}
              onNext={handleNext}
            />
            
            {/* Centered Close Button */}
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleClose}
                className="flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Close
              </Button>
            </div>
          </div>
        )}

        {/* If no navigation, show regular close button */}
        {!(sessions && typeof currentIndex === "number") && (
          <div className="flex justify-end mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleClose}
              className="flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Close
            </Button>
          </div>
        )}

        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl">{session.title}</DialogTitle>
          <div className="text-sm text-muted-foreground">{formattedDate}</div>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Session metadata */}
          <SessionMetadata
            formattedDate={formattedDate}
            formattedDuration={formattedDuration}
            professor={session.professor}
            location={session.location}
          />

          <Separator />

          {/* Competition Details - Show for all competition sessions */}
          {isCompetition && (
            <>
              <CompetitionDetails
                totalMatches={session.competitionTotalMatches}
                totalWins={session.competitionTotalWins}
                weightClasses={session.competitionWeightClasses}
                finish={session.competitionFinish}
                matches={session.competitionMatches}
              />
              <Separator />
            </>
          )}

          {/* YouTube Video */}
          {session.youtubeUrl && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Video className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-medium">Training Video</h3>
              </div>
              <YouTubeVideoPlayer url={session.youtubeUrl} />
            </div>
          )}

          {/* Techniques */}
          <SessionTechniques techniques={session.techniques} />

          {/* Reflection sections */}
          <SessionReflections
            learned={session.learned}
            improvements={session.improvements}
            successes={session.successes}
            notes={session.notes}
          />
        </div>

        {onEdit && (
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={handleEdit} className="w-full sm:w-auto">
              Edit Session
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
